import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { EstadoTrabajo, TrabajoProgramado } from '@laser/core';
import { useCatalogo, usePrograma, useTrabajos } from '../api/hooks';
import type { FiltrosApiTrabajos, TrabajoResumen } from '../api/tipos';
import { Cargando, Chip, ErrorCaja, Insignia } from '../componentes/ui';
import { ESTADOS, ETIQUETA_ESTADO } from '../lib/estados';
import { fechaCorta, hora, relativoDias } from '../lib/fechas';
import { contarPorEstado, filtrosAParams, filtrosDesdeParams, hayFiltros, ordenarPorCompromiso, type FiltrosTrabajos } from '../lib/filtros';
import { pesos } from '../lib/formato';
import { atrasoDe, maquinaDeTrabajo, totalACobrar } from '../lib/trabajo';

export default function Trabajos() {
  const [params, setParams] = useSearchParams();
  const filtros = useMemo(() => filtrosDesdeParams(params), [params]);
  const navegar = useNavigate();
  const cat = useCatalogo();
  const programa = usePrograma();

  const setFiltros = (f: FiltrosTrabajos) => setParams(filtrosAParams(f), { replace: true });
  const cambiar = (parcial: Partial<FiltrosTrabajos>) => setFiltros({ ...filtros, ...parcial });

  // Búsqueda con pausa para no pedir en cada tecla.
  const [q, setQ] = useState(filtros.q ?? '');
  useEffect(() => { setQ(filtros.q ?? ''); }, [filtros.q]);
  useEffect(() => {
    const id = setTimeout(() => { if ((filtros.q ?? '') !== q.trim()) cambiar({ q: q.trim() || undefined }); }, 300);
    return () => clearTimeout(id);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const { orden, ...filtrosApi } = filtros;
  const consulta = useTrabajos(filtrosApi as FiltrosApiTrabajos);
  const paginas = consulta.data?.pages ?? [];
  const lista = useMemo(() => {
    const todos: TrabajoResumen[] = paginas.flatMap((p) => p.trabajos).map((t) => (t.cliente ? t : { ...t, cliente: cat.cliente.get(t.clienteId) }));
    const ordenada = ordenarPorCompromiso(todos, orden);
    if (filtros.estado) return ordenada;
    // Sin filtro de estado: primero lo pendiente; lo entregado/cancelado al final, lo más reciente arriba.
    const cerrado = (t: TrabajoResumen) => t.estado === 'entregado' || t.estado === 'cancelado';
    return [...ordenada.filter((t) => !cerrado(t)), ...ordenada.filter(cerrado).reverse()];
  }, [paginas, orden, cat.cliente, filtros.estado]);

  const conteos = paginas[0]?.conteos ?? (filtros.estado ? undefined : contarPorEstado(lista));
  const porTrabajo = useMemo(() => new Map<string, TrabajoProgramado>((programa.data?.trabajos ?? []).map((p) => [p.trabajoId, p])), [programa.data]);
  const ahora = Date.now();
  const tarde = lista.filter((t) => atrasoDe(t, porTrabajo.get(t.id), ahora)).length;
  const totalConteos = conteos ? Object.values(conteos).reduce((s, n) => s + (n ?? 0), 0) : lista.length;

  return (
    <>
      <div className="titulo">
        <h1>Trabajos</h1>
        <span className="sub">{totalConteos} en total{tarde ? ` · ${tarde} con atraso en esta vista` : ''}</span>
        <div className="grow" />
        <Link className="btn" to="/programa">¿Qué corto ahora?</Link>
      </div>

      <div className="tiles" role="group" aria-label="Filtrar por estado">
        <button type="button" className="tile" aria-pressed={!filtros.estado} onClick={() => cambiar({ estado: undefined })}>
          <b>{totalConteos}</b><span>todos</span>
        </button>
        {ESTADOS.map((e) => (
          <button key={e} type="button" className="tile" aria-pressed={filtros.estado === e} onClick={() => cambiar({ estado: filtros.estado === e ? undefined : e })}>
            <b>{conteos?.[e] ?? '–'}</b><span>{ETIQUETA_ESTADO[e].toLowerCase()}</span>
          </button>
        ))}
      </div>

      <div className="filtros">
        <input type="search" placeholder="Buscar cliente, teléfono, correo o folio…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar" />
        <select value={filtros.estado ?? ''} onChange={(e) => cambiar({ estado: (e.target.value || undefined) as EstadoTrabajo | undefined })} aria-label="Estado">
          <option value="">Todos los estados</option>
          {ESTADOS.map((e) => <option key={e} value={e}>{ETIQUETA_ESTADO[e]}</option>)}
        </select>
        <select value={filtros.maquinaId ?? ''} onChange={(e) => cambiar({ maquinaId: e.target.value || undefined })} aria-label="Máquina">
          <option value="">Todas las máquinas</option>
          {cat.maquinas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
        </select>
        <select value={filtros.materialId ?? ''} onChange={(e) => cambiar({ materialId: e.target.value || undefined })} aria-label="Material">
          <option value="">Todos los materiales</option>
          {cat.materiales.map((m) => <option key={m.id} value={m.id}>{m.nombre} {m.espesorMm} mm</option>)}
        </select>
        <input type="date" value={filtros.desde ?? ''} onChange={(e) => cambiar({ desde: e.target.value || undefined })} aria-label="Recibido desde" title="Recibido desde" />
        <input type="date" value={filtros.hasta ?? ''} onChange={(e) => cambiar({ hasta: e.target.value || undefined })} aria-label="Recibido hasta" title="Recibido hasta" />
        <select value={filtros.urgente === undefined ? '' : filtros.urgente ? '1' : '0'} onChange={(e) => cambiar({ urgente: e.target.value === '' ? undefined : e.target.value === '1' })} aria-label="Urgencia">
          <option value="">Urgentes y normales</option>
          <option value="1">Sólo urgentes</option>
          <option value="0">Sólo normales</option>
        </select>
        {hayFiltros(filtros) && <button type="button" className="btn btn--sm btn--fantasma" onClick={() => { setQ(''); setFiltros({ orden }); }}>Quitar filtros</button>}
      </div>

      {consulta.isError && <ErrorCaja error={consulta.error} reintentar={() => consulta.refetch()} />}
      {consulta.isLoading ? <Cargando /> : (
        <>
          <div className="tabla-caja tabla-trabajos">
            <table>
              <thead>
                <tr>
                  <th aria-sort={orden === 'compromiso_desc' ? 'descending' : 'ascending'}>
                    <button type="button" onClick={() => cambiar({ orden: orden === 'compromiso_desc' ? undefined : 'compromiso_desc' })}>
                      Compromiso {orden === 'compromiso_desc' ? '↓' : '↑'}
                    </button>
                  </th>
                  <th className="oc2">Folio</th>
                  <th>Cliente</th>
                  <th className="oc">Material</th>
                  <th className="oc">Máquina</th>
                  <th>Estado</th>
                  <th className="num oc2">Total</th>
                </tr>
              </thead>
              <tbody>
                {lista.length === 0 && <tr><td colSpan={7} className="vacio">No hay trabajos con estos filtros.</td></tr>}
                {lista.map((t) => {
                  const atraso = atrasoDe(t, porTrabajo.get(t.id), ahora);
                  const mat = cat.material.get(t.solicitud.materialId);
                  const maqId = maquinaDeTrabajo(t);
                  return (
                    <tr key={t.id} className="clic" onClick={() => navegar(`/trabajos/${t.id}`)}>
                      <td className="celda-2">
                        <b style={{ whiteSpace: 'nowrap' }}>{fechaCorta(t.fechaCompromiso)} · {hora(t.fechaCompromiso)}</b>
                        <small>{relativoDias(t.fechaCompromiso)}</small>{' '}
                        {atraso && <Insignia tono="bad" title={atraso.titulo}>{atraso.texto}</Insignia>}
                      </td>
                      <td className="oc2"><Link className="folio" to={`/trabajos/${t.id}`} onClick={(e) => e.stopPropagation()}>{t.folio}</Link></td>
                      <td className="celda-2">
                        <b>{t.cliente?.nombre ?? '—'}</b>
                        <small>{t.cliente?.telefono}</small>{' '}
                        {t.urgente && <Insignia tono="accent">Urgente</Insignia>}{' '}
                        {t.prioridadManual > 0 && <Insignia tono="brand" title="Prioridad manual">P{t.prioridadManual}</Insignia>}
                      </td>
                      <td className="oc">{mat ? `${mat.nombre} ${mat.espesorMm} mm` : '—'}{t.solicitud.materialDelCliente && <small className="muted"> · del cliente</small>}</td>
                      <td className="oc">
                        <span className={t.maquinaAsignadaId ? undefined : 'muted'} title={t.maquinaAsignadaId ? 'Asignada' : 'Sugerida por la cotización'}>
                          {cat.maquina.get(maqId)?.nombre ?? '—'}
                        </span>
                      </td>
                      <td><Chip estado={t.estado} /></td>
                      <td className="num oc2">{pesos(totalACobrar(t))}{t.precioFinalCentavos != null && <small className="muted" title="Precio ajustado a mano"> *</small>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="tarjetas-trabajo">
            {lista.length === 0 && <p className="vacio">No hay trabajos con estos filtros.</p>}
            {lista.map((t) => {
              const atraso = atrasoDe(t, porTrabajo.get(t.id), ahora);
              const mat = cat.material.get(t.solicitud.materialId);
              return (
                <Link key={t.id} to={`/trabajos/${t.id}`} className="tt">
                  <div className="tt__l1">
                    <b>{t.cliente?.nombre ?? t.folio}</b>
                    <Chip estado={t.estado} />
                  </div>
                  <div className="tt__l2">
                    <span>{fechaCorta(t.fechaCompromiso)} · {mat ? `${mat.nombre} ${mat.espesorMm} mm` : ''}</span>
                    <span className="num">{pesos(totalACobrar(t))}</span>
                  </div>
                  <div className="tt__l2">
                    <span className="folio">{t.folio}</span>
                    <span className="fila" style={{ gap: 4 }}>
                      {t.urgente && <Insignia tono="accent">Urgente</Insignia>}
                      {atraso && <Insignia tono="bad">{atraso.texto}</Insignia>}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="fila fila--entre" style={{ marginTop: 10 }}>
            <span className="meta">{lista.length} {lista.length === 1 ? 'trabajo' : 'trabajos'}{consulta.isFetching && !consulta.isFetchingNextPage ? ' · actualizando…' : ''}</span>
            {consulta.hasNextPage && (
              <button type="button" className="btn" onClick={() => consulta.fetchNextPage()} disabled={consulta.isFetchingNextPage}>
                {consulta.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}
